import React, { createContext, useContext, useEffect, useCallback } from "react";

const AnalyticsContext = createContext(null);

// simple helper hook so components can easily fire events
export const useAnalytics = () => useContext(AnalyticsContext);

export const AnalyticsProvider = ({ children }) => {
  // ensure window.dataLayer always exists
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
  }, []);

  /**
   *  track() — primary programmatic API for components
   *  Example: analytics.track("add_to_cart", { item_id, item_name, price });
   */
  const track = useCallback((eventName, eventData = {}) => {
    if (typeof window === "undefined" || !window.dataLayer) return;

    const payload = {
      event: eventName,
      event_timestamp: Date.now(),
      page_path: window.location.pathname,
      ...eventData,
    };

    window.dataLayer.push(payload);

    if (process.env.NODE_ENV === "development") {
      console.log("📊 dataLayer event:", payload);
    }
  }, []);

  /**
   *  Optional generic click listener for `data-gtm-*` attributes
   *  Lets you tag any element in markup without manual JS
   */
  useEffect(() => {
    const handleClick = (e) => {
      const el = e.target.closest("[data-gtm-event]");
      if (!el) return;

      const eventName = el.getAttribute("data-gtm-event");
      const label = el.getAttribute("data-gtm-label") || null;
      const extra = el.getAttribute("data-gtm-extra") || null;

      track(eventName || "click_element", {
        element_name: label,
        element_extra: extra,
      });
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [track]);

  return (
    <AnalyticsContext.Provider value={{ track }}>
      {children}
    </AnalyticsContext.Provider>
  );
};
